import {
  useId,
  useRef,
  useEffect,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'
import { Link } from 'react-router-dom'
import type { SnapshotEntry } from '~/lib/api/types'
import { useCurrentUser } from '~/lib/auth/use-current-user'
import { formatLastSeen, formatShortDate } from '~/lib/time/format'
import { MoodEmoji } from '~/components/mood-emoji'
import { HoursBar } from '~/components/hours-bar'
import { ExpandedDetail } from '~/components/expanded-detail'
import styles from './row.module.scss'

type Props = {
  entry: SnapshotEntry
  expandedSlackId: string | null
  onToggle: (slackId: string) => void
}

export function Row({ entry, expandedSlackId, onToggle }: Props) {
  const slackId = entry.user.slack_user_id
  const expanded = expandedSlackId === slackId
  const detailId = useId()

  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (expanded && ref.current) {
      ref.current.setAttribute('aria-expanded', 'true')
    }
  }, [expanded])

  function handleKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onToggle(slackId)
    } else if (e.key === 'Escape' && expanded) {
      e.preventDefault()
      onToggle(slackId)
    }
  }

  return (
    <div className={styles.outer}>
      <div
        ref={ref}
        className={`${styles.row} ${styles[entry.status]} ${expanded ? styles.expanded : ''}`}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-controls={detailId}
        onClick={() => onToggle(slackId)}
        onKeyDown={handleKey}
      >
        {entry.status === 'working' && <RowWorking entry={entry} />}
        {entry.status === 'out' && <RowOut entry={entry} />}
        {entry.status === 'missing' && <RowMissing entry={entry} />}
      </div>
      {expanded && (
        <div id={detailId} className={styles.detail}>
          <ExpandedDetail entry={entry} />
        </div>
      )}
    </div>
  )
}

function RowWorking({ entry }: { entry: SnapshotEntry }) {
  const slackPermalink = entry.check_in?.id ? `slack://open` : null

  return (
    <>
      <MoodEmoji mood={entry.check_in?.mood ?? null} />
      <DisplayName
        name={entry.user.display_name ?? entry.user.slack_user_id}
        slackId={entry.user.slack_user_id}
      />
      <HoursBar
        hoursParsed={entry.hours_parsed}
        parseStatus={entry.check_in?.parse_status}
        hoursRaw={entry.check_in?.hours_raw ?? null}
        slackPermalink={slackPermalink}
      />
      <Chevron />
    </>
  )
}

function RowOut({ entry }: { entry: SnapshotEntry }) {
  const isHoliday = entry.time_off?.kind === 'holiday'
  // The page swaps in <HolidayBanner /> when company_holiday is set, so
  // an :out row with no time_off here is always a weekend — the holiday
  // case never reaches the column tree.
  const sub = entry.time_off ? outDescription(entry.time_off) : 'weekend'

  return (
    <>
      <span
        className={`${styles.shape} ${isHoliday ? styles.shapeSquareOpen : styles.shapeFilled}`}
        aria-hidden
      />
      <DisplayName
        name={entry.user.display_name ?? entry.user.slack_user_id}
        slackId={entry.user.slack_user_id}
      />
      <span className={styles.sublabel}>{sub}</span>
      <Chevron />
    </>
  )
}

function RowMissing({ entry }: { entry: SnapshotEntry }) {
  const lastSeen = entry.check_in?.submitted_at
    ? entry.check_in.submitted_at.slice(0, 10)
    : null

  return (
    <>
      <span className={`${styles.shape} ${styles.shapeRing}`} aria-hidden />
      <DisplayName
        name={entry.user.display_name ?? entry.user.slack_user_id}
        slackId={entry.user.slack_user_id}
      />
      <span className={styles.sublabel}>{formatLastSeen(lastSeen)}</span>
      <Chevron />
    </>
  )
}

function DisplayName({ name, slackId }: { name: string; slackId: string }) {
  const { data: me } = useCurrentUser()
  const isAdmin = me?.user.is_admin ?? false

  if (!isAdmin) {
    return <span className={styles.name}>{name}</span>
  }

  // Stop propagation so clicking the name navigates without also toggling
  // the row's expanded state (the parent row is the click target).
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => e.stopPropagation()
  return (
    <Link to={`/people/${slackId}`} className={styles.name} onClick={onClick}>
      {name}
    </Link>
  )
}

function outDescription(
  timeOff: NonNullable<SnapshotEntry['time_off']>
): string {
  if (timeOff.start_date === timeOff.end_date) {
    return timeOff.kind
  }
  return `${timeOff.kind} through ${formatShortDate(timeOff.end_date)}`
}

function Chevron() {
  return (
    <svg
      className={styles.chevron}
      width="10"
      height="10"
      viewBox="0 0 10 10"
      aria-hidden
      focusable="false"
    >
      <path
        d="M3 1L7 5L3 9"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
